import { format } from 'date-fns'

import { schemas, ZInfer } from '@showrunner/scrapi'

import { formatMoney } from '#util'

import { SHOW_TIERS } from '../../../choo-app/lib/helpers.js'

export type BillingPlan = ZInfer<typeof schemas.BillingPlan>
export type StaffBillingPlan = ZInfer<typeof schemas.StaffBillingPlan>
export type BillingStatus = BillingPlan['status']

export const LABELED_TIERS = [
  [SHOW_TIERS.FREE, 'Free'],
  [SHOW_TIERS.NEW_MEDIA, 'Small Budget / New Media'],
  [SHOW_TIERS.TV_FILM, 'Big Budget / TV & Film'],
  [SHOW_TIERS.STRIPE, 'Stripe'],
]

export const STRIPE_TIER: string = SHOW_TIERS.STRIPE

export const canUpgradeOrg = (
  org: { tier?: string; owner?: { id: string } | null } | undefined,
  userId: string,
): boolean => !!org && (org.tier !== STRIPE_TIER || org.owner?.id === userId)

export type PaidFeature = {
  name: string
  plural?: boolean
  available?: boolean
  plans?: string[]
}

export type PaidNote = { features: PaidFeature[]; plans: string[] }

export const paidNote = (
  features: PaidFeature[],
  { isUnpaid, tier }: { isUnpaid: boolean; tier?: string },
): PaidNote | null => {
  const missing = features.filter(({ available = true }) => !available)

  if (!isUnpaid) {
    if (missing.length === 0) return null
    // named plans are stripe products; legacy tiers upgrade by inquiry
    const plans =
      tier === STRIPE_TIER
        ? [...new Set(missing.flatMap((f) => f.plans ?? []))]
        : []
    return { features: missing, plans }
  }

  if (missing.length === 0 && tier !== STRIPE_TIER) return null

  return { features, plans: [] }
}

export const STATUS_LABELS: Record<BillingStatus, string> = {
  incomplete: 'Awaiting payment confirmation',
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment overdue',
  unpaid: 'Payment failed',
  paused: 'Paused',
  canceled: 'Canceled',
  unknown: 'Unrecognized status',
}

const LIVE_STATUSES: ReadonlySet<string> = new Set<BillingStatus>([
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'paused',
])

export const isLiveSubscription = (plan: BillingPlan): boolean =>
  LIVE_STATUSES.has(plan.status)

export const isCanceling = (plan: BillingPlan): boolean =>
  plan.cancelAt !== null && isLiveSubscription(plan)

export const isCancelled = (plan: BillingPlan): boolean =>
  plan.cancelAt !== null &&
  (plan.status === 'active' || plan.status === 'trialing')

export const renewsBeforeCanceling = (plan: BillingPlan): boolean =>
  isLiveSubscription(plan) &&
  plan.cancelAt !== null &&
  plan.currentPeriodEnd !== null &&
  plan.cancelAt > plan.currentPeriodEnd

export const isExpired = (plan: BillingPlan): boolean =>
  plan.status === 'canceled'

export const formatPlanDate = (date: Date): string =>
  format(date, 'MMM d, yyyy')

export type ScheduledPlan = NonNullable<BillingPlan['scheduledPlan']>

export const scheduledPlanName = (scheduled: ScheduledPlan): string =>
  scheduled.planName ?? 'a new plan'

// members over the seat limit of the scheduled plan
export const membersToRemove = (
  plan: BillingPlan | null | undefined,
): number => {
  const seatMax = plan?.scheduledPlan?.seatMax
  return seatMax ? Math.max(0, (plan?.usedSeats ?? 0) - seatMax) : 0
}

const INTERVAL_SUFFIX: Record<NonNullable<BillingPlan['interval']>, string> = {
  month: '/month',
  year: '/year',
}

const ratePhrase = (plan: BillingPlan, minorUnits: number): string => {
  const money = formatMoney(minorUnits, plan.currency)
  return plan.interval ? `${money}${INTERVAL_SUFFIX[plan.interval]}` : money
}

export const seatRate = (plan: BillingPlan): string => {
  if (plan.interval !== 'year') return ratePhrase(plan, plan.unitAmount)

  const perMonth = formatMoney(Math.round(plan.unitAmount / 12), plan.currency)
  return `${perMonth}/month, billed annually`
}

export const subscriptionRate = (plan: BillingPlan): string | null => {
  if (!plan.seats) return null
  return ratePhrase(plan, plan.unitAmount * plan.seats)
}

const discountOff = (discount: NonNullable<BillingPlan['discount']>): string =>
  discount.kind === 'percent'
    ? `${discount.percentOff}% off`
    : `${formatMoney(discount.amountOff, discount.currency)} off`

export const discountLabel = (plan: BillingPlan): string | null => {
  const { discount } = plan
  if (!discount || plan.promotional) return null

  const off = discountOff(discount)
  return discount.name ? `${discount.name}: ${off}` : off
}

export const STATUS_COLORS: Record<BillingStatus, string> = {
  incomplete: 'yellow',
  trialing: 'blue',
  active: 'green',
  past_due: 'orange',
  unpaid: 'red',
  paused: 'gray',
  canceled: 'red',
  unknown: 'gray',
}
